import { formatCurrency } from "@gigpro/utils";
// The posting page's draft (CORE-8650). It lives in the browser: the page shows it, edits it and posts it, and the chat
// drives the same page through page actions. Nothing here knows the chat, so the page can move onto the manual Post page.
import { convertToUtc, shortenTimezone } from "@gigpro/utils/datetime";
import type { TipPolicy } from "api/resources/shared";
import { addDays, format, parseISO } from "date-fns";
import { MAX_WAGE } from "utils/validation";
import type { Pro, SectionManager } from "../providers/PostGigData/types";
import type { Repeat } from "./repeat";

export type RowKey = "days" | "time" | "positions" | "where" | "manager" | "attachments";

/** The rows in the order a card shows them (round 56). */
export const ROW_ORDER: ReadonlyArray<RowKey> = ["days", "time", "positions", "where", "manager", "attachments"];

/** The three rows that open together on a new card and fold together once all are answered. */
export const ESSENTIAL_ROWS: ReadonlyArray<RowKey> = ["time", "days", "positions"];

export type PostPosition = {
  key: string;
  position_id?: string | null;
  position_label?: string | null;
  template_id?: string | null;
  count: number;
  /** The wage chosen: a saved position's own, else the manager's. */
  pay_rate?: number | null;
  /** Last time's wage, for a role added from the location's own positions: the wage picker recommends it. */
  last_wage?: number | null;
  /** Last time's description, for the same: while the role still holds it, its line says it came from last time. */
  last_description?: string | null;
  tip_policy?: TipPolicy | null;
  description?: string | null;
  notes?: string | null;
  requirement_ids?: Array<number> | null;
  attachment_ids?: Array<string> | null;
  uniform?: NonNullable<Pro["uniform"]> | null;
  auto_select_enabled?: boolean | null;
  audience_type?: "MARKETPLACE" | "NETWORK" | "PRIVATE_DIRECT_OFFER" | null;
  backup_to_marketplace?: boolean | null;
  selected_pro_ids?: Array<number> | null;
  /** The shifts a team captain or event lead manages: their permissions are sent after the creates. */
  manageable_gig_ids?: Array<number> | null;
};

export type ParkingType = "FREE" | "REIMBURSED" | "NOT_REIMBURSED";

export type PostShift = {
  key: string;
  /** The one day of a one-day shift, `YYYY-MM-DD` in the location's time. */
  date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  unpaid_break_minutes?: number | null;
  address_id?: string | null;
  parking_expense_type?: ParkingType | null;
  parking_instructions?: string | null;
  parking_attachment_ids?: Array<string> | null;
  /** The manager on duty, as the form holds one: a saved manager, or one still to be promoted. */
  manager?: SectionManager | null;
  attachment_ids?: Array<string> | null;
  /** Behind `agent-web-posting-recurring`: the shift repeats as a series. Absent or null posts one shift. */
  repeat?: Repeat | null;
  positions: Array<PostPosition>;
  /** Rows the manager has touched: a row is red only after that, or after Review was pressed. */
  touched?: Partial<Record<RowKey, true>>;
  /** Fields the manager set by hand (see hand-set.ts): the chat asks before changing them. */
  hand_set?: Array<string>;
  /** Fields the page filled that nobody has given since (see defaults.ts), named as `hand_set` names them. */
  defaults?: Array<string>;
};

/** What happened to one position of one shift on posting: the record a retry resumes from, and never re-creates a 2xx. */
export type PostRecord = {
  outcome: "posted" | "not_posted" | "not_confirmed";
  /** The `Idempotency-Key` the create went out under. */
  idempotency_key: string;
  /** When the first request went out: the key's 120 seconds run from it. */
  first_sent_at: number;
  gig_id?: number;
  message?: string;
  /** The Schedule was checked after the key's window and holds no such shift: safe to post again, or to drop. */
  confirmed_missing?: boolean;
  /** Why the create did not confirm: a reload resumes the right way (a lost connection resends the key). */
  failure_kind?: string;
  /** The offer to Preferred workers that follows a posted role, recorded apart from it and never undoing it. */
  offer?: {
    outcome: "sent" | "failed" | "not_confirmed" | "promoted";
    idempotency_key: string;
    message?: string;
    /** Sent, but the service refused some of the pros: why, one reason per kind, with no pro named. */
    refused?: Array<string>;
    /** A refused offer the manager closed the question on: not asked again, though it still was not sent. */
    dismissed?: boolean;
    /** Refused because the location has no way to pay: the receipt asks for a payment method, never re-offers. */
    payment_missing?: boolean;
  };
  /** Its gig was cancelled after it posted. The outcome stays posted, so the role stays sent and never posts again. */
  cancelled?: boolean;
  cancelled_at?: number;
};

export type PostDraft = {
  location_id: string;
  shifts: Array<PostShift>;
  po_number?: string | null;
  /** `"<shift key>/<position key>"` -> the record of posting it. */
  records?: Record<string, PostRecord>;
  /** Review was pressed: every unmet row shows red from then on. */
  reviewed?: boolean;
  /** When the draft last changed, for the day it expires and for telling a task still in hand from an old one. */
  saved_at?: number | null;
  /**
   * The values the chat overwrote (D3), kept on the draft itself so a reload keeps them and an undo rewinds them, and never
   * in a shift, which the review's revision and the sent-role check read. Keyed `card/positions/role` or `card/row/field`.
   */
  marks?: Record<string, RowMark>;
  /** The posting session (`client_context.posting_session`): new with each fresh draft, and again once it posts. */
  session?: string;
  /** What the stored draft's fields mean (`DRAFT_VERSION`); none is a draft from before versions. */
  version?: number;
};

/**
 * 2: on the section cards an empty map list sends no map; before, it meant the venue's own maps.
 * 3: a role's tips answer is `tip_policy`; before, it was the yes/no `includes_tips`.
 */
export const DRAFT_VERSION = 3;

/** A role as a draft from before `tip_policy` stored it. */
type LegacyPosition = PostPosition & { includes_tips?: boolean | null };

/** A role's old yes/no tips answer, kept as the policy it meant; a role that already has a policy keeps it. */
const currentPosition = (position: LegacyPosition): PostPosition => {
  if (!("includes_tips" in position)) return position;
  const { includes_tips: includesTips, ...rest } = position;
  if (rest.tip_policy != null || includesTips == null) return rest;
  return { ...rest, tip_policy: includesTips ? "INCLUDED" : "NOT_INCLUDED" };
};

/** A stored draft's shift read the current way, the same object when nothing in it is old. */
const currentShift = (shift: PostShift, version: number): PostShift => {
  const emptyMaps = version < 2 && shift.parking_attachment_ids?.length === 0;
  const legacyTips = shift.positions.some((position) => "includes_tips" in position);
  if (!emptyMaps && !legacyTips) return shift;
  return {
    ...shift,
    ...(emptyMaps && { parking_attachment_ids: null }),
    ...(legacyTips && { positions: shift.positions.map(currentPosition) }),
  };
};

/**
 * A stored draft read the current way: an older draft's empty map list goes back to meaning the venue's own maps, and
 * a role's yes/no tips answer becomes the policy it meant. The tips answer is read whatever the version, so a draft an
 * older page saved again after this one still keeps it.
 */
export const currentDraft = (draft: PostDraft): PostDraft => {
  const version = draft.version ?? 1;
  const shifts = draft.shifts.map((shift) => currentShift(shift, version));
  if (version >= DRAFT_VERSION && shifts.every((shift, i) => shift === draft.shifts[i])) return draft;
  return { ...draft, version: DRAFT_VERSION, shifts };
};

/** A row the chat changed after someone set it (`changed`), or a chat undo put back (`put_back`): what it held before. */
export type RowMark = {
  kind: "changed" | "put_back";
  /** The role's name, or the row's value in words ("the time"). */
  name: string;
  /** Each value it held before, in words, by what it is ("count" -> "2 pros"), in the order they changed. */
  was: Record<string, string>;
};

/** A role whose gig was cancelled after it posted: still a posted record, so it never posts again, but said as cancelled. */
export const isCancelledPost = (record: PostRecord | undefined) => record?.outcome === "posted" && !!record.cancelled;
/** A role that posted and is still live. */
export const isLivePost = (record: PostRecord | undefined) => record?.outcome === "posted" && !record.cancelled;

let counter = 1;
export const newKey = (prefix: string) => `${prefix}${Date.now().toString(36)}${(counter++).toString(36)}`;

export const emptyShift = (): PostShift => ({ key: newKey("s"), positions: [] });
export const newPostingSession = () => crypto.randomUUID();

export const emptyDraft = (locationId: number): PostDraft => ({
  location_id: String(locationId),
  shifts: [emptyShift()],
  session: newPostingSession(),
  version: DRAFT_VERSION,
});

/** Whether the draft holds nothing the manager chose: the opening chips show only then. */
export const isEmptyDraft = (draft: PostDraft) =>
  draft.shifts.every(
    (shift) =>
      !shift.date &&
      !shift.start_time &&
      shift.positions.length === 0 &&
      !shift.attachment_ids?.length &&
      Object.keys(shift.touched ?? {}).length === 0,
  );

/** The day it is at the location, `YYYY-MM-DD`; without a zone, the browser's. */
export const todayIn = (timezone: string | null | undefined, now: Date) =>
  timezone
    ? new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(now)
    : format(now, "yyyy-MM-dd");

/** A start typed as wall-clock time is a moment at the location, not in the browser: its zone decides when it is. */
const instantAt = (start: Date, timezone: string | null | undefined) =>
  timezone ? convertToUtc(start, timezone) : start;

const atTime = (date: string, time: string) => {
  const withSeconds = time.length === 5 ? `${time}:00` : time;
  return parseISO(`${date}T${withSeconds}`);
};

/** The shift's start and end as dates; an end at or before the start is the next day, as on the form. */
export const shiftWindow = (shift: Pick<PostShift, "date" | "start_time" | "end_time">) => {
  if (!shift.date || !shift.start_time || !shift.end_time) return null;
  const start = atTime(shift.date, shift.start_time);
  const end = atTime(shift.date, shift.end_time);
  return { start, end: end <= start ? addDays(end, 1) : end };
};

const timeLabel = (time: string) => {
  const [hh = 0, mm = 0] = time.split(":").map(Number);
  const hour = hh % 12 === 0 ? 12 : hh % 12;
  return `${hour}:${String(mm).padStart(2, "0")} ${hh < 12 ? "AM" : "PM"}`;
};

/** "5:00 – 11:00 PM": the shared meridiem is said once, as the cards name their time; an overnight time says both. */
export const timeRange = (start: string, end: string) => {
  const from = timeLabel(start);
  const to = timeLabel(end);
  const sameDay = end.slice(0, 5) > start.slice(0, 5);
  return sameDay && from.slice(-2) === to.slice(-2) ? `${from.slice(0, -3)} – ${to}` : `${from} – ${to}`;
};

/** Who a role goes to as the page posts it: a role stored on a network goes to the Marketplace where Engage is off. */
export const audienceOf = (position: Pick<PostPosition, "audience_type">, engageOn: boolean) =>
  position.audience_type === "NETWORK" && !engageOn ? "MARKETPLACE" : (position.audience_type ?? "MARKETPLACE");

export const AUDIENCE_WORDS = {
  MARKETPLACE: "Marketplace",
  PRIVATE_DIRECT_OFFER: "Preferred",
  NETWORK: "Network",
} as const;

/** A posted role's gig as the offer dialog takes it; null before it has a gig and a time. */
export const offerGigOf = (
  shift: PostShift,
  position: PostPosition,
  record: PostRecord | undefined,
  locationId: number,
) => {
  const window = shiftWindow(shift);
  if (record?.gig_id == null || !window) return null;
  return {
    id: record.gig_id,
    skill: position.position_label ?? "Position",
    skill_id: Number(position.position_id ?? 0),
    start_time: window.start,
    end_time: window.end,
    wage_amount: position.pay_rate ?? null,
    location_id: locationId,
    type: "PRIVATE_DIRECT_OFFER" as const,
    is_recurring: !!shift.repeat,
  };
};

/** The posted roles whose offer the preferred pros refused and the manager has yet to answer, as the offer dialog takes their gigs. */
export const refusedOffersOf = (draft: PostDraft, locationId: number) =>
  draft.shifts.flatMap((shift) =>
    shift.positions.flatMap((position) => {
      const entry = `${shift.key}/${position.key}`;
      const record = draft.records?.[entry];
      const gig = offerGigOf(shift, position, record, locationId);
      if (record?.offer?.outcome !== "failed" || record.offer.dismissed || !gig) return [];
      return [{ entry, gig }];
    }),
  );

/** A role as the page shows it: its saved position's own name first; `position_label` stays the skill the chat matches on. */
export const roleName = (
  position: Pick<PostPosition, "position_label" | "template_id">,
  savedNames?: ReadonlyMap<string, string>,
) => (position.template_id && savedNames?.get(position.template_id)) || position.position_label || "Position";

export const rolesLine = (positions: ReadonlyArray<PostPosition>, savedNames?: ReadonlyMap<string, string>) =>
  positions
    .filter(({ position_label }) => position_label)
    .map((position) => `${roleName(position, savedNames)} × ${position.count}`)
    .join(", ");

/** An end at or before the start is the next day: told from the card's own times, so a card without a day says it too. */
const overnight = ({ start_time, end_time }: Pick<PostShift, "start_time" | "end_time">) =>
  !!start_time && !!end_time && end_time.slice(0, 5) <= start_time.slice(0, 5);

/** The day after a `YYYY-MM-DD`, worked on the string in UTC: no browser zone or clock change can move it. */
const dayAfter = (day: string) => {
  const next = new Date(`${day}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
};

/** Built from the card's strings, never the browser's zone, whose spring-forward gap would move a 2:30 AM start. */
export const wallClockTimes = (day: string, start: string, end: string) => ({
  starts_at: `${day}T${start.slice(0, 5)}:00`,
  ends_at: `${overnight({ start_time: start, end_time: end }) ? dayAfter(day) : day}T${end.slice(0, 5)}:00`,
});

const named = (time: string | null, positions: ReadonlyArray<PostPosition>, savedNames?: ReadonlyMap<string, string>) =>
  [time, rolesLine(positions, savedNames) || null].filter(Boolean).join(" · ") || "New shift";

/** "10:00 PM – 4:00 AM (+1 day)": the time as the card, its Time row and Review show it. */
const shiftTime = (shift: PostShift) => {
  if (!shift.start_time || !shift.end_time) return null;
  const nextDay = overnight(shift) ? " (+1 day)" : "";
  return `${timeRange(shift.start_time, shift.end_time)}${nextDay}`;
};

/**
 * The location's zone at the shift's own start, so a shift after the clocks change reads EST; a day with no time yet at
 * its noon; without a day, now.
 */
export const zoneAt = (shift: PostShift, timezone: string | null | undefined, now: Date = new Date()) =>
  shortenTimezone(timezone, shift.date ? instantAt(atTime(shift.date, shift.start_time || "12:00"), timezone) : now);

/** The Time row's value: "10:00 PM – 4:00 AM (+1 day) (EDT)", "(+1 day)" first so a narrow row can't lose it. */
export const timeLine = (shift: PostShift, timezone: string | null | undefined) => {
  const time = shiftTime(shift);
  return time ? `${time} (${zoneAt(shift, timezone)})` : null;
};

/** The heading and region name; `cardName` stays the label the chat, toasts and notes use. */
export const cardTitle = (shift: PostShift, savedNames?: ReadonlyMap<string, string>) =>
  named(shiftTime(shift), shift.positions, savedNames);

/** A card is named by what it is: its time and its roles. A card with neither says so. */
export const cardName = (shift: PostShift) =>
  named(shift.start_time && shift.end_time ? timeRange(shift.start_time, shift.end_time) : null, shift.positions);

const ZONE_SUFFIX = / (Standard|Daylight) Time$/;

/** "America/New_York" as people say it: "Eastern time". Falls back to the zone's own name. */
export const timezoneName = (timezone: string, at: Date = new Date()) => {
  try {
    const long = new Intl.DateTimeFormat("en-US", { timeZone: timezone, timeZoneName: "long" })
      .formatToParts(at)
      .find(({ type }) => type === "timeZoneName")?.value;
    return long ? long.replace(ZONE_SUFFIX, " time") : timezone;
  } catch {
    return timezone;
  }
};

export const dayLabel = (date: string) => format(parseISO(date), "EEE, MMM d");

export type NeedField =
  | "day"
  | "time"
  | "position"
  | "count"
  | "pay"
  | "description"
  | "tips"
  | "parking"
  | "where"
  | "manager"
  | "break"
  | "pros";
export type NeedLabel =
  | "Needs tips"
  | "Needs day"
  | "Needs time"
  | "Needs a position"
  | "Needs pay"
  | "Needs description"
  | "Needs an address"
  | "Needs parking"
  | "Needs arrival"
  | "Needs manager"
  | "Needs pros";
export type Need = { row: RowKey; field: NeedField; label: NeedLabel; roleKey?: string };

/** Arrival instructions are required on every shift today; the page and each card read this one rule. */
export const ARRIVAL_REQUIRED = true;

/** What a shift still lacks, in the order the rows run. The page decides when to show it (no red before the manager acts). */
export const shiftNeeds = (
  shift: PostShift,
  rules: {
    arrivalRequired: boolean;
    tipsRequired?: (positionId: string | null | undefined) => boolean;
    /** The arrival instructions saved on an address: a card with none of its own uses them, as the classic form does. */
    savedArrival?: (addressId: string) => string | null | undefined;
  },
): Array<Need> => {
  const needs: Array<Need> = [];
  if (!shift.date) needs.push({ row: "days", field: "day", label: "Needs day" });
  if (!shift.start_time || !shift.end_time) needs.push({ row: "time", field: "time", label: "Needs time" });
  if (shift.positions.length === 0) needs.push({ row: "positions", field: "position", label: "Needs a position" });
  for (const position of shift.positions) {
    // $0 is no pay: a draft kept with one still needs it chosen.
    if (!position.pay_rate || position.pay_rate <= 0) {
      needs.push({ row: "positions", field: "pay", label: "Needs pay", roleKey: position.key });
    }
    // The API refuses a position with a blank description; a skill with no default one leaves it to the manager.
    if (!position.description?.trim()) {
      needs.push({ row: "positions", field: "description", label: "Needs description", roleKey: position.key });
    }
    if (rules.tipsRequired?.(position.position_id) && position.tip_policy == null) {
      needs.push({ row: "positions", field: "tips", label: "Needs tips", roleKey: position.key });
    }
    // Preferred with nobody picked would post to no one; classic's form refuses it too.
    if (position.audience_type === "PRIVATE_DIRECT_OFFER" && !position.selected_pro_ids?.length) {
      needs.push({ row: "positions", field: "pros", label: "Needs pros", roleKey: position.key });
    }
  }
  if (!shift.address_id) needs.push({ row: "where", field: "where", label: "Needs an address" });
  else if (!shift.parking_expense_type) needs.push({ row: "where", field: "parking", label: "Needs parking" });
  const arrival = shift.parking_instructions ?? (shift.address_id ? rules.savedArrival?.(shift.address_id) : null);
  if (rules.arrivalRequired && !arrival?.trim()) {
    needs.push({ row: "where", field: "where", label: "Needs arrival" });
  }
  if (!shift.manager) needs.push({ row: "manager", field: "manager", label: "Needs manager" });
  return needs;
};

/** `shiftNeeds`' saved-arrival rule from the location's addresses. */
export const savedArrivalIn =
  (addresses: ReadonlyArray<{ id: number; parking_instructions?: string | null }> | undefined) => (addressId: string) =>
    addresses?.find(({ id }) => String(id) === addressId)?.parking_instructions;

export type ProblemCode =
  | "over_shift_max"
  | "over_hourly_max"
  | "same_as_start"
  | "in_the_past"
  | "too_soon"
  | "break_too_long";
export type Problem = { row: RowKey; field: NeedField; code: ProblemCode; message: string; roleKey?: string };

/** The minutes ahead a shift must start, as the form's own check has it. */
const MIN_LEAD_MINUTES = 15;

/** "$540", or "$500.04" when it isn't whole. */
const shiftPay = (amount: number) => {
  const cents = Math.round(amount * 100);
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
};

/**
 * What is wrong with what the shift holds, in the form's words: a role over the API's maximum per shift or per hour, a start
 * that has passed, one less than 15 minutes out, an end the same as the start, a break longer than the shift. Not a missing
 * field: a row is red for it only once the manager has touched it or pressed Review. Role problems come first, in role order.
 */
export const shiftProblems = (
  shift: PostShift,
  now: Date,
  timezone?: string | null,
  /** The roles whose wage the API refuses as over its maximum. */
  overCap: ReadonlySet<string> = new Set(),
  savedNames?: ReadonlyMap<string, string>,
): Array<Problem> => {
  const window = shiftWindow(shift);
  // The server's span: the venue's instants, so a clock-change night is 11 or 13 hours; no break taken off.
  const hours = window
    ? (instantAt(window.end, timezone).getTime() - instantAt(window.start, timezone).getTime()) / 3_600_000
    : null;
  return [
    ...shift.positions.flatMap((position) => roleProblems(shift.key, position, hours, overCap, savedNames)),
    ...timeProblems(shift, window, now, timezone),
  ];
};

const roleProblems = (
  shiftKey: string,
  position: PostPosition,
  hours: number | null,
  overCap: ReadonlySet<string>,
  savedNames?: ReadonlyMap<string, string>,
): Array<Problem> => {
  if (position.count <= 0 || position.pay_rate == null) return [];
  const problems: Array<Problem> = [];
  const name = roleName(position, savedNames);
  if (overCap.has(`${shiftKey}/${position.key}`)) {
    problems.push({
      row: "positions",
      field: "pay",
      code: "over_hourly_max",
      roleKey: position.key,
      message: `${name}: ${formatCurrency(position.pay_rate)}/hr is above the maximum hourly wage allowed`,
    });
  }
  const total = hours == null ? 0 : position.pay_rate * hours;
  // Same float math as the API (sidegig-api payments/services/costs.py pro_wage, validators.py MAX_PRO_WAGE): the page never offers Post for a shift the API refuses.
  if (total > MAX_WAGE) {
    problems.push({
      row: "positions",
      field: "pay",
      code: "over_shift_max",
      roleKey: position.key,
      message: `${name} exceeds the $${MAX_WAGE} maximum per shift (${shiftPay(total)}).`,
    });
  }
  return problems;
};

const timeProblems = (
  shift: PostShift,
  window: ReturnType<typeof shiftWindow>,
  now: Date,
  timezone?: string | null,
): Array<Problem> => {
  const problems: Array<Problem> = [];
  if (shift.start_time && shift.end_time && shift.start_time === shift.end_time) {
    problems.push({ row: "time", field: "time", code: "same_as_start", message: "Cannot be the same as start time" });
  }
  const startsAt = window ? instantAt(window.start, timezone).getTime() : null;
  if (startsAt != null && startsAt <= now.getTime()) {
    problems.push({ row: "time", field: "time", code: "in_the_past", message: "Cannot be in the past" });
  } else if (startsAt != null && startsAt - now.getTime() < MIN_LEAD_MINUTES * 60_000) {
    problems.push({ row: "time", field: "time", code: "too_soon", message: "Must be at least 15 minutes out" });
  }
  // Gig.clean refuses only strictly longer; its own field keeps setDay and the copy fill from reading it as "in the past".
  if (window && (shift.unpaid_break_minutes ?? 0) * 60_000 > window.end.getTime() - window.start.getTime()) {
    problems.push({
      row: "time",
      field: "break",
      code: "break_too_long",
      message: "Unpaid break cannot be longer than the shift",
    });
  }
  return problems;
};

export type ProblemLink = "lower_pay" | "shorten_shift" | "remove_role";

/** The fixes a problem offers, as the snapshot's contract lists them; Shorten shift only while the card's time can change. */
export const problemLinks = (problem: Pick<Problem, "code">, timeLocked: boolean): Array<ProblemLink> => {
  if (problem.code === "over_shift_max") {
    return timeLocked ? ["lower_pay", "remove_role"] : ["lower_pay", "shorten_shift", "remove_role"];
  }
  return problem.code === "over_hourly_max" ? ["lower_pay", "remove_role"] : [];
};

/** A role that went out, or may have: its gig is the server's now, so nothing about it gates the page. */
const sentRecord = (record: PostRecord | undefined) =>
  record?.outcome === "posted" || record?.outcome === "not_confirmed";

/**
 * The problems that still gate Post: none on a role that went out or may have, or on one with nobody to hire, and none at all
 * on a card with nothing left to send. The footer, the run, the card, Review, the chat's post and the snapshot all read this.
 * On a partly posted card the time is locked, so a time or break problem says the way out: remove, then post it anew.
 */
export const unsentProblems = (
  shift: PostShift,
  records: Readonly<Record<string, PostRecord>> | undefined,
  problems: ReadonlyArray<Problem>,
  savedNames?: ReadonlyMap<string, string>,
): Array<Problem> => {
  const sent = (position: PostPosition) => sentRecord(records?.[`${shift.key}/${position.key}`]);
  const left = shift.positions.filter((position) => position.count > 0 && !sent(position));
  if (shift.positions.length > 0 && left.length === 0) return [];
  const partly = shift.positions.some(sent);
  const names = left.map((position) => roleName(position, savedNames)).join(" and ");
  const them = left.length === 1 ? "it" : "them";
  return problems
    .filter(({ roleKey }) => !roleKey || left.some(({ key }) => key === roleKey))
    .map((problem) =>
      partly && !problem.roleKey
        ? {
            ...problem,
            message: `Can't post ${names} with this time. Remove ${them}, then Post another to post ${them} as a new shift.`,
          }
        : problem,
    );
};

/** What gates a card's post now: its problems (`shiftProblems`) less what went out (`unsentProblems`). Every reader uses this. */
export const gatingProblems = (
  shift: PostShift,
  records: PostDraft["records"],
  now: Date,
  timezone?: string | null,
  overCap?: ReadonlySet<string>,
  savedNames?: ReadonlyMap<string, string>,
): Array<Problem> =>
  unsentProblems(shift, records, shiftProblems(shift, now, timezone, overCap, savedNames), savedNames);

/** The start has passed or is under 15 minutes out: the post's own check refuses it before anything else runs. */
export const startTooSoon = (shift: PostShift, now: Date, timezone?: string | null) => {
  const window = shiftWindow(shift);
  return !!window && instantAt(window.start, timezone).getTime() - now.getTime() < MIN_LEAD_MINUTES * 60_000;
};

/** The three essentials are answered: time, day, and every position with its pay. */
export const essentialsDone = (shift: PostShift) =>
  !!shift.date &&
  !!shift.start_time &&
  !!shift.end_time &&
  shift.positions.length > 0 &&
  shift.positions.every((p) => !!p.pay_rate && p.pay_rate > 0);

/** Days that have passed are set aside; today stays while its start has not passed. */
export const hasPassed = (shift: Pick<PostShift, "date" | "start_time">, now: Date, timezone?: string | null) => {
  if (!shift.date) return false;
  if (shift.date < todayIn(timezone, now)) return true;
  return !!shift.start_time && instantAt(atTime(shift.date, shift.start_time), timezone) <= now;
};

export const peopleCount = (shifts: ReadonlyArray<PostShift>) =>
  shifts.reduce((total, shift) => total + shift.positions.reduce((sum, { count }) => sum + count, 0), 0);

/** What a change to a file list says: more ("attached"), none left, fewer ("removed"), or as many but other files ("changed"). */
export const filesChange = (
  before: ReadonlyArray<string>,
  after: ReadonlyArray<string>,
  words: { more: string; none: string; fewer: string; other: string },
) => {
  if (after.length === 0) return words.none;
  if (after.length > before.length) return words.more;
  return after.length < before.length ? words.fewer : words.other;
};

export const MAP_WORDS = {
  more: "Map or instructions attached",
  none: "No map or instructions",
  fewer: "Map or instructions removed",
  other: "Map or instructions changed",
};
export const ATTACHMENT_WORDS = {
  more: "Attachments added",
  none: "No attachments",
  fewer: "Attachments removed",
  other: "Attachments changed",
};
